/**
 * Every tab's 3D "home" (MASTER_PROMPT §6.2, Phase 6), as data. No DOM, no
 * three.js; unit-tested in Node. For each tab: the endpoints it reads and a
 * build(data, snap) that returns a small diorama:
 *
 *   parts    3D parts in buildgen's format { shape: "box"|"cyl"|"ring"|"dome",
 *            x, y, z, w, h, d, color, ref, glow } (ring adds r0, r1, a0, a1)
 *   labels   { text, sub, x, y, z, ref, hot }
 *   facts    the same story in words: { label, value, tone } (tone: pos|neg|warn)
 *   actions  real moves: { ref, label, go } where go is
 *            { tab } | { clickText } (a button on the tab) | { product } | { scroll }
 *
 * Tapping a part runs the action with the same ref. Every size and colour is
 * a real number from the backend; scales are said in `scale`.
 */
import { district, interior, PALETTE } from "../world/buildgen.js";

const C = { you: 0x7ee8fa, pos: 0x34d399, warn: 0xfbbf24, neg: 0xfb7185, civic: 0xd8d0bd, steel: 0x8b93ad, dark: 0x232a48, gold: 0xe5b44f, violet: 0xa78bfa };
const money = (v) => `${v < 0 && Math.round(Math.abs(v)) ? "-" : ""}${(() => { const a = Math.abs((v || 0)); return a >= 1e12 ? `$${(a / 1e12).toFixed(2)}T` : a >= 1e9 ? `$${(a / 1e9).toFixed(2)}B` : a >= 1e6 ? `$${(a / 1e6).toFixed(2)}M` : a >= 1e4 ? `$${(a / 1e3).toFixed(0)}K` : `$${Math.round(a).toLocaleString("en-US")}`; })()}`; // short money (2.7)
const shortMoney = (v) => { const a = Math.abs(v || 0); return a >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : a >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : money(v); };
const pct = (v, d = 1) => `${(+v || 0).toFixed(d)}%`;
const toneOf = (score) => (score >= 65 ? "pos" : score >= 40 ? "warn" : "neg");
const colorOf = (score) => (score >= 65 ? C.pos : score >= 40 ? C.warn : C.neg);
const box = (x, y, z, w, h, d, color, extra) => ({ shape: "box", x, y, z, w, h, d, color, ...extra });
const cyl = (x, y, z, w, h, color, extra) => ({ shape: "cyl", x, y, z, w, h, d: w, color, ...extra });
const floor = (w, d) => box(0, -0.2, 0, w, 0.2, d, PALETTE.ground, { floor: true });
/** Items spread on an arc in front of the camera. */
const arc = (n, r = 7, span = Math.PI * 0.9) => Array.from({ length: n }, (_, i) => {
  const a = n === 1 ? 0.7 : -span / 2 + (span * i) / (n - 1); // a lone item sits to the side, not hidden behind
  return [Math.sin(a) * r, -Math.cos(a) * r * 0.55];
});

const SPECS = {
  overview: {
    title: "Company vitals", endpoints: ["/company-health", "/alerts"],
    build({ "/company-health": h, "/alerts": alerts }) {
      const parts = [floor(26, 16)], labels = [], actions = [];
      const beam = colorOf(h.score);
      parts.push(box(0, 0, 0, 3.4, 9, 3.4, C.you, { ref: "hq" }), box(0, 9, 0, 2.2, 1.6, 2.2, PALETTE.youDark, { ref: "hq" }));
      parts.push(cyl(0, 10.6, 0, 0.5, 2.4 + (h.score / 100) * 5, beam, { ref: "hq", glow: 1 }));
      labels.push({ text: `Health ${Math.round(h.score)}/100`, sub: `${h.label} (the beacon)`, x: 0, y: 14, z: 0, ref: "hq" });
      (alerts || []).slice(0, 4).forEach((a, i) => {
        const [x, z] = arc(Math.min(4, alerts.length), 9)[i];
        const sev = a.severity === "critical" ? C.neg : C.warn;
        parts.push(box(x, 0, z, 2.2, 3 + i * 0.4, 2.2, PALETTE.filler, { ref: `alert:${i}` }), cyl(x, 3.6 + i * 0.4, z, 1.6, 0.25, sev, { ref: `alert:${i}`, glow: 1 }));
        labels.push({ text: a.category, sub: a.message, x, y: 5, z, ref: `alert:${i}`, hot: true });
        actions.push({ ref: `alert:${i}`, label: `${a.category}: open`, go: { tab: a.tab } });
      });
      const facts = [{ label: "Company health", value: `${Math.round(h.score)}/100 · ${h.label}`, tone: toneOf(h.score) },
        ...Object.entries(h.components || {}).map(([k, v]) => ({ label: k.replace(/_/g, " "), value: `${Math.round(v)}`, tone: toneOf(v) })),
        { label: "Priority alerts", value: `${(alerts || []).length}`, tone: (alerts || []).length ? "warn" : "pos" }];
      return { parts, labels, facts, actions, scale: "Beacon height and colour = company health. Each pulsing building is a priority alert." };
    },
  },

  ratios: {
    title: "Instrument cluster", endpoints: [],
    build(_, snap) {
      const r = snap.ratios || {};
      const gauges = [
        ["Gross margin", r.gross_margin_pct, 0, 100, (v) => v >= 40, "%"],
        ["Net margin", r.net_margin_pct, -30, 40, (v) => v > 0, "%"],
        ["Current ratio", Math.min(r.current_ratio, 5), 0, 5, (v) => v >= 1.2, "x"],
        ["Debt / equity", r.debt_to_equity, 0, 3, (v) => v <= 1.5, "x"],
        ["Interest cover", Math.max(-5, Math.min(r.interest_coverage, 20)), -5, 20, (v) => v >= 3, "x"],
      ];
      const parts = [floor(24, 10)], labels = [], facts = [];
      gauges.forEach(([name, v, lo, hi, good, unit], i) => {
        const x = -9.6 + i * 4.8, ok = v != null && good(v);
        const t = v == null ? 0 : Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
        // an upright dial: dark face, a dim full track, and the value arc
        parts.push(box(x, 0, 0, 3.8, 3.9, 0.3, C.dark, { ref: `g:${i}` }),
          { shape: "ring", upright: true, x, y: 2, z: 0.16, r0: 1.15, r1: 1.5, a0: -Math.PI * 0.75, a1: Math.PI * 0.75, h: 0.08, color: 0x39415e, ref: `g:${i}` },
          { shape: "ring", upright: true, x, y: 2, z: 0.2, r0: 1.1, r1: 1.55, a0: -Math.PI * 0.75, a1: -Math.PI * 0.75 + Math.PI * 1.5 * Math.max(0.02, t), h: 0.12, color: ok ? C.pos : C.neg, ref: `g:${i}`, glow: 1 });
        const shown = name === "Current ratio" && r.current_ratio > 5 ? `${(+r.current_ratio).toFixed(1)}x` : v == null ? "—" : unit === "%" ? pct(v) : `${(+v).toFixed(2)}x`;
        labels.push({ text: name, sub: shown, x, y: 4.6, z: 0.3, ref: `g:${i}`, hot: !ok });
        facts.push({ label: name, value: shown, tone: ok ? "pos" : "neg" });
      });
      return { parts, labels, facts, actions: [], scale: "Each dial fills toward its healthy end; green = healthy by the usual rule of thumb, red = needs attention." };
    },
  },

  banking: {
    title: "Debt & credit", endpoints: ["/debt-desk"],
    build({ "/debt-desk": d }) {
      const parts = [floor(28, 18)], labels = [], actions = [];
      const t = d.totals || {};
      parts.push(box(0, 0, 0, 3, 5, 3, C.you, { ref: "hq" }));
      labels.push({ text: "Your company", sub: `${shortMoney(t.cash)} cash`, x: 0, y: 6.5, z: 0, ref: "hq" });
      const loans = (d.loans || []).slice(0, 6);
      const max = Math.max(1, ...loans.map((l) => l.outstanding || l.outstanding_balance || 0));
      loans.forEach((l, i) => {
        const bal = l.outstanding || l.outstanding_balance || 0;
        const [x, z] = arc(loans.length, 10)[i];
        const hgt = 3 + 5 * (bal / max);
        parts.push(box(x, 0, z, 2.6, hgt, 2.6, C.civic, { ref: `loan:${i}` }), box(x, hgt, z, 3, 0.5, 3, C.gold, { ref: `loan:${i}` }));
        // the bridge: width = what you still owe
        const len = Math.hypot(x, z) - 3, ang = Math.atan2(x, z);
        parts.push({ ...box(x / 2, 2.4, z / 2, 0.3 + 1.6 * (bal / max), 0.25, len, C.gold, { ref: `loan:${i}` }), rotY: ang });
        labels.push({ text: l.lender || l.bank_name, sub: `${shortMoney(bal)} at ${pct((l.rate || l.interest_rate || 0) * 100, 1)}`, x, y: hgt + 1.5, z, ref: `loan:${i}` });
        actions.push({ ref: `loan:${i}`, label: `See ${l.lender || l.bank_name}`, go: { scroll: ".bk-loans, .bk-loan, table" } });
      });
      if (!loans.length) labels.push({ text: "No loans", sub: "Bridges appear when you borrow", x: 6, y: 3, z: -3 });
      actions.push({ ref: "hq", label: "Borrow", go: { clickText: "Borrow" } });
      const facts = [{ label: "Debt", value: money(t.debt) }, { label: "Monthly debt service", value: money(t.monthly_debt_service) },
        { label: "Credit rating", value: (d.credit && d.credit.rating) || "—", tone: /distress|CCC|^B /.test((d.credit && d.credit.rating) || "") ? "neg" : "pos" },
        { label: "Debt / equity", value: `${(+t.debt_to_equity || 0).toFixed(2)}x` }];
      return { parts, labels, facts, actions, scale: "Tower height and bridge width = what you still owe each lender." };
    },
  },

  publicmarkets: {
    title: "Stock exchange", endpoints: ["/public-company", "/board/desk"],
    build({ "/public-company": p }, snap) {
      const parts = [floor(22, 12)], labels = [];
      parts.push(box(0, 0, 0, 10, 5, 5, C.civic, { ref: "x" }), box(0, 5, 0, 10.8, 0.8, 5.8, C.gold, { ref: "x" }));
      for (let i = 0; i < 6; i++) parts.push(cyl(-4 + i * 1.6, 0, 2.7, 0.5, 5, 0xfbf9f3, { ref: "x" }));
      const isPublic = p && !p.detail;
      const val = (snap.ratios && snap.ratios.valuation) || snap.valuation || 0;
      labels.push({ text: isPublic ? "Listed" : "Not listed yet", sub: isPublic ? `Share price ${money(p.share_price || p.price || 0)}` : `Valuation ${shortMoney(val)}`, x: 0, y: 7.5, z: 0, ref: "x" });
      const facts = isPublic
        ? [{ label: "Share price", value: money(p.share_price || p.price || 0) }, { label: "Market cap", value: shortMoney(p.market_cap || 0) }]
        : [{ label: "Valuation", value: shortMoney(val) }, { label: "Status", value: (p && p.detail) || "Private", tone: "warn" }];
      return { parts, labels, facts, actions: [], scale: "The exchange lights up when you list (Phase 8 brings the IPO bell)." };
    },
  },

  operations: {
    title: "Operations", endpoints: ["/world/hq", "/operations/desk"],
    build({ "/world/hq": w }) {
      const kit = w.kit || {};
      const cap = w.capacity;
      const util = cap ? cap.utilization_pct : (w.sites.factories || []).reduce((m, f) => Math.max(m, f.utilization_pct || 0), 0) || null;
      const d = interior(w.sites, (kit.interior && kit.interior.kind) || "office", { utilization: util, floorLabel: kit.interior && kit.interior.label });
      const facts = [{ label: "Floor", value: (kit.interior && kit.interior.label) || "Office" },
        ...(util != null ? [{ label: "Utilization", value: pct(util), tone: util > 100 ? "neg" : util < 40 ? "warn" : "pos" }] : []),
        ...(w.bottlenecks || []).map((b) => ({ label: "Bottleneck", value: b.text, tone: "neg" }))];
      return { parts: d.parts, labels: d.labels, facts, actions: [{ ref: "world", label: "See it in the World", go: { tab: "world" } }], scale: "One station per person; red = over capacity, dim = idle.", size: d.size };
    },
  },

  executives: {
    title: "Team & talent", endpoints: ["/world/hq", "/people/desk"],
    build({ "/world/hq": w, "/people/desk": p }) {
      const d = interior(w.sites, "office", {});
      const facts = [{ label: "People", value: `${p.headcount}` }, { label: "Morale", value: `${Math.round(p.morale)}/100`, tone: toneOf(p.morale) },
        { label: "Poaching pressure", value: pct(p.poaching_pressure_pct, 0), tone: p.poaching_pressure_pct > 50 ? "neg" : "pos" },
        { label: "Empty exec seats", value: `${(p.empty_seats || []).length}`, tone: (p.empty_seats || []).length ? "warn" : "pos" }];
      return { parts: d.parts, labels: d.labels, facts, actions: [{ ref: "hire", label: "Hire", go: { clickText: "Hire" } }], scale: "One desk per person, zoned by department; corner offices for executives.", size: d.size };
    },
  },

  competitors: {
    title: "Competitors", endpoints: ["/competitors"],
    build({ "/competitors": list }) {
      const rivals = (list || []).filter((r) => r.status === "Active").slice(0, 8);
      const parts = [floor(28, 14)], labels = [];
      const max = Math.max(1, ...rivals.map((r) => r.market_share_pct || 0));
      rivals.forEach((r, i) => {
        const [x, z] = arc(rivals.length, 10)[i], hgt = 2 + 12 * ((r.market_share_pct || 0) / max);
        parts.push(box(x, 0, z, 2.4, hgt, 2.4, C.neg, { ref: `r:${i}` }), box(x, hgt, z, 1.6, 0.6, 1.6, 0x8a3a4f, { ref: `r:${i}` }));
        labels.push({ text: r.name, sub: `${pct(r.market_share_pct)} · ${r.strategy}`, x, y: hgt + 1.4, z, ref: `r:${i}` });
      });
      parts.push(box(0, 0, 3, 2.6, 4, 2.6, C.you, { ref: "you" }));
      labels.push({ text: "You", x: 0, y: 5, z: 3, ref: "you" });
      const facts = rivals.slice(0, 5).map((r) => ({ label: r.name, value: `${pct(r.market_share_pct)} share` }));
      return { parts, labels, facts, actions: [{ ref: "you", label: "See them in your district", go: { tab: "world" } }], scale: "Tower height = market share." };
    },
  },

  consulting: {
    title: "Consulting report", endpoints: ["/consulting-report"],
    build({ "/consulting-report": c }) {
      const r = (c && c.report) || {};
      const n = ["strategy", "finance", "operations", "marketing", "workforce", "legal"].reduce((s, k) => s + (r[k] || []).length, 0);
      return { parts: [floor(20, 14)], labels: [], facts: [{ label: "Findings", value: `${n}` }], actions: [], scale: "Each finding comes with what to do about it below." };
    },
  },

  risk: {
    title: "Risk & compliance", endpoints: ["/risk/register"],
    build({ "/risk/register": r }) {
      const parts = [floor(24, 16)], labels = [], actions = [];
      parts.push(box(0, 0, 0, 3, 6, 3, C.you, { ref: "hq" }));
      parts.push({ shape: "dome", x: 0, y: 0, z: 0, w: 14, h: 7, d: 14, color: 0x7ee8fa, opacity: 0.08 + 0.3 * ((r.compliance_score || 0) / 100), ref: "shield" });
      labels.push({ text: `Compliance ${Math.round(r.compliance_score)}`, sub: `Cyber defence ${Math.round(r.cyber_defense_level)} (the dome)`, x: 0, y: 8.5, z: 0, ref: "shield" });
      const open = (r.register || []).filter((x) => !x.insured).sort((a, b) => b.monthly_probability_pct - a.monthly_probability_pct).slice(0, 4);
      open.forEach((x, i) => {
        const [bx, bz] = arc(open.length, 9)[i];
        parts.push({ ...box(bx, 4, bz, 0.3, 6 * Math.min(1, x.monthly_probability_pct / 5) + 1, 0.3, C.warn, { ref: `risk:${i}`, glow: 1 }), rotZ: 0.35 });
        labels.push({ text: x.label, sub: `${pct(x.monthly_probability_pct, 2)} a month · uninsured`, x: bx, y: 9, z: bz, ref: `risk:${i}`, hot: true });
        actions.push({ ref: `risk:${i}`, label: `Insure: ${x.label}`, go: { clickText: "Insure" } });
      });
      const facts = [{ label: "Compliance", value: `${Math.round(r.compliance_score)}/100`, tone: toneOf(r.compliance_score) },
        { label: "Cyber defence", value: `${Math.round(r.cyber_defense_level)}/100`, tone: toneOf(r.cyber_defense_level) },
        { label: "Uninsured risks", value: `${(r.register || []).filter((x) => !x.insured).length}`, tone: open.length ? "warn" : "pos" }];
      return { parts, labels, facts, actions, scale: "Dome strength = compliance score; each lightning bolt is an uninsured risk, longer = likelier." };
    },
  },

  board: {
    title: "Boardroom", endpoints: ["/board/desk"],
    build({ "/board/desk": b }) {
      const parts = [floor(20, 14)], labels = [];
      parts.push(box(0, 0, 0, 9, 1.1, 3.4, 0x5a4632, { ref: "table" }), cyl(0, 1.1, 0, 1.2, 0.15, colorOf(b.confidence), { ref: "table", glow: 1 }));
      (b.directors || []).forEach((d, i) => {
        const x = -3.6 + i * 1.8, z = i % 2 ? 2.6 : -2.6;
        parts.push(box(x, 0, z, 1.1, 1.0, 1.1, C.dark, { ref: `d:${i}` }), cyl(x, 1.0, z, 0.7, 1.4, colorOf(d.mood), { ref: `d:${i}` }));
        labels.push({ text: d.name, sub: `${d.title} · ${d.mood_label}`, x, y: 3.2, z, ref: `d:${i}`, hot: d.mood < 40 });
      });
      const facts = [{ label: "Confidence", value: `${Math.round(b.confidence)}/100 · ${b.verdict}`, tone: toneOf(b.confidence) },
        { label: "Next review", value: `month ${b.next_review_month} (in ${b.months_to_review})` },
        ...(b.directors || []).map((d) => ({ label: d.title, value: d.mood_label, tone: toneOf(d.mood) }))];
      return { parts, labels, facts, actions: [], scale: "Table light = board confidence; each director is lit by their mood." };
    },
  },

  investors: {
    title: "Ownership & investors", endpoints: ["/investors/desk"],
    build({ "/investors/desk": v }) {
      const parts = [floor(18, 14)], labels = [];
      const entries = (v.entries || []).filter((e) => (e.pct || e.ownership_pct || 0) > 0);
      const colors = [C.you, C.pos, C.gold, C.violet, 0xf472b6, 0x9fb4ff];
      let a = 0;
      entries.forEach((e, i) => {
        const share = (e.pct != null ? e.pct : e.ownership_pct) / 100;
        const span = share * Math.PI * 2;
        parts.push({ shape: "ring", x: 0, y: 0, z: 0, r0: 2.6, r1: 5, a0: a, a1: a + span, h: 0.6 + share * 2, color: colors[i % colors.length], ref: `cap:${i}` });
        const mid = a + span / 2;
        labels.push({ text: e.holder || e.name || e.type, sub: pct(share * 100), x: Math.sin(mid) * 5.8, y: 2.6, z: -Math.cos(mid) * 5.8, ref: `cap:${i}` });
        a += span;
      });
      const facts = [{ label: "Sentiment", value: `${Math.round(v.sentiment)}/100 · ${v.verdict}`, tone: toneOf(v.sentiment) },
        { label: "Valuation", value: shortMoney(v.valuation) }, { label: "You own", value: pct(v.founder_pct) }];
      return { parts, labels, facts, actions: [{ ref: "raise", label: "Raise money", go: { clickText: "Raise" } }], scale: "Each slice = a holder's share; taller = bigger stake." };
    },
  },

  deals: {
    title: "Deal pipeline", endpoints: ["/deals/desk"],
    build({ "/deals/desk": dd }) {
      const open = ((dd && dd.deals) || []).filter((d) => d.stage !== "Signed" && d.stage !== "Lost").slice(0, 6);
      const parts = [floor(20, 12), box(0, 0, 0, 10, 1.0, 3, 0x2f3b5c, { ref: "table" })], labels = [];
      parts.push(cyl(0, 0, -2.4, 0.9, 1.6, C.you, { ref: "you" }));
      labels.push({ text: "You", x: 0, y: 2.6, z: -2.4, ref: "you" });
      open.forEach((d, i) => {
        const x = -((open.length - 1) * 1.8) / 2 + i * 1.8;
        parts.push(cyl(x, 0, 2.4, 0.8, 1.5, C.gold, { ref: `deal:${i}` }));
        labels.push({ text: d.company || "Partner", sub: `${d.type || "deal"} · ${d.stage}`, x, y: 2.6, z: 2.4, ref: `deal:${i}` });
      });
      if (!open.length) labels.push({ text: "Empty chairs", sub: "Request a meeting to fill them", x: 0, y: 2, z: 2.4 });
      const k = (dd && dd.kpis) || {};
      const facts = [{ label: "Open negotiations", value: `${open.length}` }, { label: "Weighted pipeline", value: `${shortMoney(k.weighted_pipeline_arr)}/yr` },
        { label: "Signed", value: `${k.signed || 0}`, tone: k.signed ? "pos" : "" }];
      // no button here: the one way to meet a company is the meeting section below (Rachit, 2026-10-15)
      return { parts, labels, facts, actions: [], scale: "Each stage of the pipeline; a stack per deal, height = yearly value, colour = chance." };
    },
  },

  ma: {
    title: "Mergers & acquisitions", endpoints: ["/ma/threads"],
    build({ "/ma/threads": m }) {
      const items = (m.opportunities || []).slice(0, 6);
      const parts = [floor(26, 12)], labels = [];
      const price = (o) => { const s = (o.paragraphs || []).join(" ").match(/\$([\d.]+)([MBK]?)/); return s ? parseFloat(s[1]) * ({ M: 1e6, B: 1e9, K: 1e3 }[s[2]] || 1) : 0; };
      const max = Math.max(1, ...items.map(price));
      items.forEach((o, i) => {
        const x = -((items.length - 1) * 3.2) / 2 + i * 3.2, hgt = 2 + 9 * (price(o) / max);
        parts.push(box(x, 0, 0, 2.4, hgt, 2.4, C.steel, { ref: `t:${i}` }), box(x, hgt, 0, 2.6, 0.5, 0.1, C.gold, { ref: `t:${i}`, glow: 1 }));
        labels.push({ text: o.subject.replace(/^Acquisition opportunity: /, ""), sub: price(o) ? `asking ${shortMoney(price(o))}` : "for sale", x, y: hgt + 1.2, z: 0, ref: `t:${i}` });
      });
      if (!items.length) labels.push({ text: "Nothing for sale this month", x: 0, y: 2, z: 0 });
      const facts = [{ label: "For sale", value: `${items.length}` }, { label: "Companies you own", value: `${(m.summary && m.summary.owned) || 0}` },
        { label: "Cash + what you could borrow", value: shortMoney(((m.summary && m.summary.cash) || 0) + ((m.summary && m.summary.max_loan) || 0)) }];
      return { parts, labels, facts, actions: [], scale: "Tower height = asking price; the gold tag marks it for sale." };
    },
  },

  government: {
    // light on purpose (Rachit, 2026-10-15): only your own government contacts, no 196-country world download
    title: "Government relations", endpoints: ["/government/threads"],
    build({ "/government/threads": g }) {
      const rel = (g.relations || []);
      const here = rel.filter((r) => r.status === "hq" || r.status === "operating");
      const invited = rel.filter((r) => r.status === "invited");
      const facts = [{ label: "Countries you're in", value: `${here.length}` }, { label: "Invitations", value: `${invited.length}`, tone: invited.length ? "warn" : "" },
        { label: "Signed deals", value: `${(g.summary && g.summary.signed) || 0}` }];
      return { parts: [floor(28, 12)], labels: [], facts, actions: [{ ref: "world", label: "Open the World", go: { tab: "world" } }], scale: "Your government contacts: trust and tax in the countries you deal with." };
    },
  },

  news: {
    title: "Front page", endpoints: ["/news"],
    build({ "/news": n }) {
      const items = [...(n.company || []).slice(0, 2), ...(n.world || []).slice(0, 4)].slice(0, 6);
      const parts = [floor(24, 8)], labels = [];
      items.forEach((it, i) => {
        const x = -7.5 + (i % 3) * 7.5, y = i < 3 ? 4.6 : 0.6;
        parts.push(box(x, y, 0, 7, 3.6, 0.3, i < (n.company || []).length && i < 2 ? 0x1e3a5f : C.dark, { ref: `n:${i}` }));
        labels.push({ text: it.headline || it.title || it.message, sub: it.section || "Your company", x: x - 3.2, y: y + 3, z: 0.4, ref: `n:${i}` });
      });
      return { parts, labels, facts: [{ label: "Headlines", value: `${items.length}` }], actions: [], scale: "Your company's stories are the dark blue screens." };
    },
  },
};

SPECS.macro = { title: "Global economy", endpoints: ["/macro-economy"], build({ "/macro-economy": m }) {
  const rows = (m.countries || []).slice(0, 6);
  const CYC = { Expansion: C.pos, Stable: C.you, Overheating: C.warn, Tightening: 0xf6b26b, Slowdown: C.neg, Recession: C.neg };
  const parts = [floor(26, 10)], labels = [];
  rows.forEach((c, i) => {
    const x = -((rows.length - 1) * 4) / 2 + i * 4, g = c.gdp_growth_pct || 0, hgt = 1 + Math.max(0, g) * 1.6;
    const col = CYC[c.cycle_phase] || C.steel;
    parts.push(box(x, 0, 0, 2.4, hgt, 2.4, col, { ref: `m:${i}` }));
    if (/Slowdown|Recession|Tightening/.test(c.cycle_phase)) parts.push({ shape: "dome", x, y: hgt + 0.6, z: 0, w: 3.2, h: 1.0, d: 3.2, color: 0x6b7495, opacity: 0.8, ref: `m:${i}` });
    labels.push({ text: c.country, sub: `${c.cycle_phase} · GDP ${pct(g, 1)} · rates ${pct(c.interest_rate_pct, 1)}`, x, y: hgt + 2.2, z: 0, ref: `m:${i}`, hot: /Slowdown|Recession/.test(c.cycle_phase) });
  });
  const facts = [{ label: "Growth effect on you", value: `${m.growth_effect_pct >= 0 ? "+" : ""}${pct(m.growth_effect_pct, 2)}`, tone: m.growth_effect_pct >= 0 ? "pos" : "neg" },
    ...rows.map((c) => ({ label: c.country, value: c.cycle_phase, tone: /Expansion|Stable/.test(c.cycle_phase) ? "pos" : "warn" }))];
  return { parts, labels, facts, actions: [{ ref: "world", label: "See the globe", go: { tab: "world" } }], scale: "Column height = GDP growth; colour = the cycle; grey clouds over a slowing economy." };
} };

SPECS.fx = { title: "Currency exposure", endpoints: ["/fx/status"], build({ "/fx/status": f }) {
  const parts = [floor(18, 14), box(0, 0, 0, 2.6, 4, 2.6, C.you, { ref: "hq" })], labels = [{ text: "USD (home)", sub: `${pct(100 - (f.total_international_exposure_pct || 0))} of revenue`, x: 0, y: 5, z: 0, ref: "hq" }];
  let a = 0;
  (f.currencies || []).forEach((c, i) => {
    const span = Math.max(0.15, (c.revenue_share_pct / 100) * Math.PI * 2);
    parts.push({ shape: "ring", x: 0, y: 0, z: 0, r0: 3.4, r1: 5.2, a0: a, a1: a + span, h: c.hedged ? 1.6 : 0.6, color: c.hedged ? C.gold : [C.violet, 0xf472b6, C.pos, C.warn][i % 4], ref: `fx:${i}` });
    const mid = a + span / 2;
    labels.push({ text: c.currency, sub: `${pct(c.revenue_share_pct)}${c.hedged ? " · hedged" : " · unhedged"}`, x: Math.sin(mid) * 6, y: 2, z: -Math.cos(mid) * 6, ref: `fx:${i}`, hot: !c.hedged });
    a += span + 0.08;
  });
  if (!(f.currencies || []).length) labels.push({ text: "All revenue in USD", sub: "Enter a market abroad to see currencies here", x: 4, y: 2, z: 0 });
  const facts = [{ label: "Earned abroad", value: pct(f.total_international_exposure_pct) },
    ...(f.currencies || []).map((c) => ({ label: c.currency, value: c.hedged ? "hedged" : "unhedged", tone: c.hedged ? "pos" : "warn" }))];
  return { parts, labels, facts, actions: [{ ref: "hedge", label: "Hedge a currency", go: { clickText: "Hedge" } }], scale: "Each slice = a foreign currency's share of revenue; gold and tall = hedged." };
} };

/** The district-based homes reuse the World's generator. */
SPECS.footprint = { title: "Global footprint", endpoints: ["/world"], build({ "/world": w }) {
  const d = district(w, `${w.company.name}|home`);
  const here = (w.countries || []).filter((c) => c.status !== "available");
  return { parts: d.parts.filter((p) => p.ref !== "scenery"), labels: d.labels.filter((l) => l.kind === "hq" || l.kind === "site" || l.kind === "hero"),
    facts: here.map((c) => ({ label: c.country, value: c.status === "hq" ? "HQ" : `${pct(c.revenue_share_pct)} of revenue` })),
    actions: [{ ref: "hq", label: "Open the 3D globe", go: { tab: "world" } }], scale: "Your home district; the World tab shows every country.", size: d.size };
} };

const HOME_TABS = Object.keys(SPECS);

export { HOME_TABS, SPECS };
